// Core SDK — works everywhere
import { initializeApp } from "firebase/app";
import { Capacitor } from "@capacitor/core";
import {
  // ✅ IMPORTANT: use initializeAuth on native to control persistence
  getAuth,
  initializeAuth,
  inMemoryPersistence,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import {
  getAnalytics,
  isSupported as isAnalyticsSupported,
} from "firebase/analytics";

// ------------------------------------------------------------------
// Public config (these keys are meant to be in client bundles; OK to show)
// ------------------------------------------------------------------
export const firebaseConfig = {
  apiKey:            "AIzaSyDxfr7NWaV7xfiDttddvGnxy1JjF_oSt9g",
  authDomain:        "satstacker-797ed.firebaseapp.com",
  projectId:         "satstacker-797ed",
  storageBucket:     "satstacker-797ed.appspot.com",
  messagingSenderId: "181069765332",
  appId:             "1:181069765332:web:bbc3650b78b6263fc03941",
  measurementId:     "G-4HH7EYVEYF",
};

// Initialise once
export const firebaseApp = initializeApp(firebaseConfig);

/**
 * ✅ KEY FIX:
 * On Capacitor (iOS/Android), avoid IndexedDB persistence because it can hang
 * in WKWebView. Use in-memory persistence for native.
 *
 * IMPORTANT: this means the Firebase session won't persist across app restarts
 * by itself. If you need persistence, you'll store your own token (which you
 * already do via NativeBiometric / your backend JWT).
 */
export const firebaseAuth = Capacitor.isNativePlatform()
  ? initializeAuth(firebaseApp, { persistence: inMemoryPersistence })
  : getAuth(firebaseApp);

export const googleProvider = new GoogleAuthProvider();
export const appleProvider = new OAuthProvider("apple.com");

// ------------------------------------------------------------------
// Analytics (web)
// ------------------------------------------------------------------
export let analytics = null;

if (typeof window !== "undefined") {
  isAnalyticsSupported()
    .then((supported) => {
      if (supported) {
        analytics = getAnalytics(firebaseApp);
      }
    })
    .catch((err) => {
      console.warn("Firebase Analytics not supported / failed to init:", err);
    });
}

// ------------------------------------------------------------------
// Messaging (web / Android only)
// ------------------------------------------------------------------
const IS_IOS = __CAPACITOR_PLATFORM__ === "ios";      // injected by Vite
export let messaging           = null;                // => null on iOS
export let getMessagingToken   = async () => null;    // safe stub
export let onForegroundMessage = () => {};            // safe stub
export const vapidKey = import.meta.env.VITE_FB_VAPID || "";

if (!IS_IOS) {
  // Dynamic import keeps the code out of the iOS bundle
  const { getMessaging, getToken, onMessage } = await import("firebase/messaging");

  messaging = getMessaging(firebaseApp);
  getMessagingToken = (options) => getToken(messaging, options);
  onForegroundMessage = (handler) => onMessage(messaging, handler);
}

export async function startProviderSignIn(provider) {
  if (Capacitor.isNativePlatform()) {
    // Note: if you're using capgo native login on mobile,
    // you probably won't call this path anyway.
    await signInWithRedirect(firebaseAuth, provider);
    return null;
  }
  return await signInWithPopup(firebaseAuth, provider);
}

// Helper: after redirect returns, call this once to finish login
export async function finishRedirectSignInIfAny() {
  try {
    const result = await getRedirectResult(firebaseAuth);
    return result || null;
  } catch (e) {
    throw e;
  }
}
